import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ChevronLeft, BookImage, Sparkles } from "lucide-react";
import { getStoredSessionId, listPhotos, fetchPhotoImage } from "../game/api";
import { FLAW_LABEL } from "../game/critiques";

const gradeBg = {
    "A+": "#1B6F1B", "A": "#2A8C2A", "B": "#B5811F", "C": "#B05015", "F": "#8A0303",
};

export default function CameraRoll() {
    const nav = useNavigate();
    const sessionId = getStoredSessionId();
    const [photos, setPhotos] = useState([]);
    const [loading, setLoading] = useState(true);
    const [images, setImages] = useState({});  // photo_id -> data_url

    useEffect(() => {
        if (!sessionId) { nav("/"); return; }
        let cancelled = false;
        (async () => {
            try {
                const p = await listPhotos(sessionId);
                if (cancelled) return;
                setPhotos(p);
                setLoading(false);
                // lazy-load each AI image
                p.filter(x => x.has_ai_image).forEach(async (ph) => {
                    try {
                        const r = await fetchPhotoImage(sessionId, ph.id);
                        if (cancelled) return;
                        if (r?.data_url) {
                            setImages(prev => ({ ...prev, [ph.id]: r.data_url }));
                        }
                    } catch (e) {
                        console.warn(`AI image fetch failed for photo ${ph.id}:`, e?.message || e);
                    }
                });
            } catch (e) {
                console.warn("Photo list fetch failed:", e?.message || e);
                if (!cancelled) setLoading(false);
            }
        })();
        return () => { cancelled = true; };
    }, [nav, sessionId]);

    return (
        <div className="min-h-screen px-4 sm:px-8 lg:px-16 py-10" data-testid="camera-roll-screen">
            <header className="flex items-center justify-between mb-8">
                <button onClick={() => nav(-1)} className="dial-btn px-3 py-1.5 flex items-center gap-1.5" data-testid="back-btn">
                    <ChevronLeft size={14} /> Back
                </button>
                <h1 className="font-display uppercase tracking-tighter text-amber text-2xl sm:text-3xl text-shadow-glow flex items-center gap-3">
                    <BookImage size={20} /> Camera Roll
                </h1>
                <span className="text-[10px] tracking-[0.3em] uppercase text-[var(--text-muted)]">
                    {loading ? "developing..." : `${photos.length} frames`}
                </span>
            </header>

            {loading && <div className="text-amber font-mono text-sm" data-testid="roll-loading">Developing prints...</div>}

            {!loading && photos.length === 0 && (
                <div className="border border-amber-30 p-8 text-center text-[var(--text-secondary)] font-mono text-sm" data-testid="roll-empty">
                    No frames developed yet. Go take some photographs.
                </div>
            )}

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="roll-grid">
                {photos.map((p, idx) => {
                    const ai = images[p.id];
                    return (
                        <article
                            key={p.id}
                            className="polaroid"
                            style={{ transform: `rotate(${(idx % 2 ? 1.4 : -1.6).toFixed(2)}deg)` }}
                            data-testid={`roll-photo-${idx}`}
                        >
                            <div className="relative w-full aspect-[4/3] overflow-hidden bg-black">
                                {ai ? (
                                    <img
                                        src={ai}
                                        alt={`${p.room_name} — developed`}
                                        className="absolute inset-0 w-full h-full object-cover"
                                        data-testid={`roll-photo-ai-${idx}`}
                                    />
                                ) : (
                                    <>
                                        <div className="absolute inset-0" style={{
                                            background: p.success
                                                ? "radial-gradient(circle at 50% 55%, rgba(255,107,0,0.5), #0a0606 70%)"
                                                : "radial-gradient(circle at 50% 55%, rgba(138,3,3,0.5), #050303 75%)",
                                        }} />
                                        <div className="absolute inset-0 flex items-center justify-center text-3xl opacity-80 text-amber-glow">
                                            {p.success ? "◉" : "✕"}
                                        </div>
                                        {p.has_ai_image && (
                                            <div className="absolute inset-0 flex items-center justify-center bg-black/60 text-amber font-mono text-[10px] tracking-[0.2em] uppercase">
                                                ⏳ Loading
                                            </div>
                                        )}
                                    </>
                                )}
                                {ai && (
                                    <span
                                        className="absolute top-1.5 right-1.5 bg-black/70 text-amber text-[8px] tracking-widest uppercase px-1.5 py-0.5 flex items-center gap-1"
                                        data-testid={`roll-photo-ai-badge-${idx}`}
                                    >
                                        <Sparkles size={9} /> AI
                                    </span>
                                )}
                            </div>
                            <div className="mt-3 flex items-center justify-between text-[10px] uppercase tracking-widest">
                                <span>{p.room_name}</span>
                                <span className="px-2 py-0.5 font-bold text-white" style={{ background: gradeBg[p.grade] || "#444" }}>
                                    {p.grade}
                                </span>
                            </div>
                            <div className="mt-1 text-[9px] tracking-widest opacity-70">
                                {p.aperture} · {p.shutter} · ISO {p.iso}
                            </div>
                            {p.flaws?.length > 0 && (
                                <div className="mt-2 text-[10px] text-[#5a0303]">
                                    {p.flaws.map(f => FLAW_LABEL[f] || f).join(" · ")}
                                </div>
                            )}
                            <div className="mt-2 text-[10px] italic opacity-70 line-clamp-2">"{p.critique}"</div>
                        </article>
                    );
                })}
            </div>
        </div>
    );
}
